import 'whatwg-fetch';

import {
    HEADER_CONTENT_TYPE,
    JSON_CONTENT_TYPE,
    STATUS_NO_CONTENT,
    HEADER_ACCEPT,
    STATUS_CREATED,
    STATUS_RESET_CONTENT,
    STATUS_ACCEPTED,
} from './constants';
import { getCsrfHeader, needsRetry } from './csrf';
import { ErrorResponse, RequestException } from './exceptions';
import RequestExceptionWithResponse from './requestExceptionWithResponse';

const isCustomJsonType = (contentType: string) => {
    const mediaType = contentType.split(';')[0];
    const [first, second] = mediaType.split('/');
    return first.indexOf('application') > -1 && second.split('+').indexOf('json') > -1;
};

const isJson = (response: Response) => {
    const contentType = response.headers.get(HEADER_CONTENT_TYPE);
    return contentType && (contentType.indexOf(JSON_CONTENT_TYPE) > -1 || isCustomJsonType(contentType));
};

const handleResponse = async <T>(response: Response): Promise<T | null> => {
    if (response.status === STATUS_NO_CONTENT) return Promise.resolve(null);
    if (isJson(response) && response.body !== null) return response.json();
    if (
        response.status === STATUS_CREATED ||
        response.status === STATUS_ACCEPTED ||
        response.status === STATUS_RESET_CONTENT
    )
        return Promise.resolve(null);
    return Promise.reject();
};

const handleError = async (response: Response): Promise<Response> => {
    if (!response.ok) {
        if (isJson(response)) {
            return response.json().then((errorResponse) => {
                throw new RequestExceptionWithResponse(response, errorResponse as ErrorResponse);
            });
        }
        throw new RequestException(response);
    }
    return Promise.resolve(response);
};

export const defaultHeaders = {
    [HEADER_CONTENT_TYPE]: JSON_CONTENT_TYPE,
    [HEADER_ACCEPT]: JSON_CONTENT_TYPE,
};

const getContextPath = (url: string) => {
    const regExp = new RegExp('/*([a-z-]+)/*');
    const match = regExp.exec(url);
    return match ? match[1] : '';
};

export const fetchResponse = (
    url: string,
    method: string,
    { headers, ...options }: RequestInit = {},
): Promise<Response> => {
    const contextPath = getContextPath(url);

    const fetchCall = () =>
        fetch(url, {
            credentials: 'same-origin',
            method,
            ...options,
            headers: { ...headers, ...getCsrfHeader(contextPath) },
        });

    return fetchCall()
        .then((response: Response) => {
            if (needsRetry(response)) {
                return fetchCall();
            }
            return response;
        })
        .then(handleError);
};

const enhancedFetch = <T>(url: string, method: string, { headers, ...options }: RequestInit = {}): Promise<T> => {
    return fetchResponse(url, method, { headers, ...options }).then(handleResponse) as Promise<T>;
};

const addDefaultHeaders = (headers = {}) => ({ ...defaultHeaders, ...headers });

export const get = <R>(url: string, options: RequestInit = {}): Promise<R> => {
    return enhancedFetch(url, 'GET', {
        ...options,
        headers: addDefaultHeaders(options.headers),
    });
};

export const put = <R>(url: string, body: unknown, { headers, ...options }: RequestInit = {}): Promise<R> => {
    return enhancedFetch(url, 'PUT', {
        body: body !== null && body !== undefined ? JSON.stringify(body) : undefined,
        ...options,
        headers: addDefaultHeaders(headers),
    });
};

export const post = <R>(url: string, body?: unknown, options: RequestInit = {}): Promise<R> => {
    return enhancedFetch(url, 'POST', {
        body: body !== null && body !== undefined ? JSON.stringify(body) : undefined,
        ...options,
        headers: addDefaultHeaders(options.headers),
    });
};

export const del = <R>(url: string, options: RequestInit = {}): Promise<R> => {
    return enhancedFetch(url, 'DELETE', {
        ...options,
        headers: addDefaultHeaders(options.headers),
    });
};

export const patch = <R>(url: string, body?: unknown, options: RequestInit = {}): Promise<R> => {
    return enhancedFetch(url, 'PATCH', {
        body: body !== null && body !== undefined ? JSON.stringify(body) : undefined,
        ...options,
        headers: addDefaultHeaders(options.headers),
    });
};

export const postFormData = <R>(url: string, formData: FormData, options: RequestInit = {}): Promise<R> => {
    return enhancedFetch(url, 'POST', {
        body: formData,
        ...options,
    });
};

export default {
    get,
    post,
    postFormData,
    put,
    del,
    patch,
};
